/**
 * Where a part plays with its mute on, and where it bows at the bridge.
 *
 * Both are said as `pizz.` is, once over the staff and holding until another word ends it
 * (`said.ts`), in the languages scores say them in:
 *
 * - **a mute.** `con sord.` -- *con sordino*, *mit Dämpfer*, *gedämpft*, *sourdine*,
 *   *muted* -- puts it on, and `senza sord.` -- *via la sordina*, *ohne Dämpfer*, *Dämpfer
 *   ab*, *sans sourdine*, *ôtez la sourdine*, *mute off*, *without mute*, *open* -- takes
 *   it off. The word is a bowed
 *   string's and a brass player's: a pianist's *senza sordino* is the dampers, and over a
 *   flute's staff it is a word.
 * - **the bridge.** `sul pont.` -- *sul ponticello*, *am Steg*, *sur le chevalet* -- takes
 *   the bow there, and any word that takes it elsewhere ends it: *ord.*, *naturale*,
 *   *gewöhnlich*, *normal*, *sul tasto*, *col legno*, and `pizz.`, after which the `arco`
 *   that follows is a plain one. It is a bow's, so a bowed string's alone.
 *
 * Neither changes a note. Each is a flag on what is played, beside the instrument as
 * plucking is, for an engine that can colour a recording; the oscillator has no timbre to
 * mute and sounds the note as it would have.
 */

import type { Part, Score } from "../document/types";
import { bowed } from "./plucking";
import { holdingOf } from "./said";
import type { Holding } from "./said";

/** The mute taken off, asked before it is put on: every one of these names it too. */
const OPEN =
  /senza\s+sord|\bs\.\s?sord|ohne\s+d[aä]mpf|d[aä]mpf\w*\b.*\b(ab|weg)(\b|\w*nehmen|\w*setzen)|sans\s+sourd|[oô]tez\b.*\bsourd|enlevez\b.*\bsourd|\b(no|without)\s+mutes?\b|\bmutes?\s+(off|out)\b|(take\s+off|remove)\s+mutes?\b|\bvia\s+(l[ae]\s+|il?\s+)?sord|\b(lev|togli)\w*\b.*\bsord|\bopen\b/i;
/** The mute put on. *Sordamente* is how a passage is played, and no mute. */
const MUTE = /sord(?!amente)|d[aä]mpf|sourd|\bmute[sd]?\b/i;
/** The bow at the bridge, and every word that takes it from there. */
const BRIDGE = /\bpont|\bam\.?\s+steg|chevalet/i;
const ELSEWHERE =
  /\bord(\b|in\.|inari|inaire)|\bnat(\.|[uü]r)|\bgew(\.|[oö]hn)|\bnormal|\btast|griffbr|sur\s+la\s+touche|col\s+legno|\bpizz/i;

/** General MIDI's brass that takes a mute: trumpet, trombone, tuba, horn, and its section. */
const BRASS: ReadonlySet<number> = new Set([56, 57, 58, 60, 61]);

const TRUMPET = 56;
/**
 * General MIDI's one program for a muted instrument, and it is a trumpet. It is what a MIDI
 * file is told for a trumpet with its mute on; every other muted instrument keeps its own,
 * because the standard has nothing nearer to tell.
 */
const MUTED_TRUMPET = 59;

/** Whether an instrument is one that takes a mute at the word. */
export function mutes(program: number | null): boolean {
  return bowed(program) || (program !== null && BRASS.has(program));
}

/** The program a MIDI file is told for an instrument with its mute on. */
export function mutedProgram(program: number | null): number | null {
  return program === TRUMPET ? MUTED_TRUMPET : program;
}

function muting(text: string): boolean | null {
  if (OPEN.test(text)) return false;
  return MUTE.test(text) ? true : null;
}

function bridging(text: string): boolean | null {
  if (BRIDGE.test(text)) return true;
  return ELSEWHERE.test(text) ? false : null;
}

/** Where `part` has its mute on, bar by bar of the written order. */
export function mutingOf(score: Score, part: Part): Holding {
  return holdingOf(score, part, muting);
}

/** Where `part` bows at the bridge, bar by bar of the written order. */
export function bridgingOf(score: Score, part: Part): Holding {
  return holdingOf(score, part, bridging);
}
